/**
 * The kitchen, on screen and under a finger.
 *
 * The one idea this whole file rests on: A CABINET HAS ONE DEGREE OF FREEDOM, NOT SIX. It
 * lives in a run, and a run is a line along a wall, so dragging a cabinet is sliding it
 * along that line. It cannot be lifted, rotated, buried in a wall or left floating, because
 * none of those are things it can do.
 *
 * That turns the hard problem -- manipulating a solid in perspective with a fingertip
 * eleven millimetres wide -- into an easy one: moving a marker along a line. It also means
 * the design is buildable by construction rather than by validation, because there is no
 * gesture that can produce an invalid kitchen.
 *
 * Camera framing is lifted from Plumbline's viewer, where it was arrived at the hard way:
 * distance comes from projecting the room's corners onto the camera's own axes rather than
 * from a bounding sphere, because a long room seen from the corner covers nowhere near its
 * longest side and framing from that leaves it small and adrift.
 */
import * as THREE from 'three'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import { buildCabinet, buildBenchtop, buildAppliance, buildLook, sizeUv, MM } from './build.js'
import { floorTexture, wallTexture, setAnisotropy } from './texture.js'
import { compose, offsets, refit, reflow, sum } from './layout.js'
import { brandOf } from './brand.js'
import * as room from './room.js'

/**
 * How thick a wall is, for the purpose of lining a doorway, in millimetres.
 *
 * The walls themselves are single-sided planes with no thickness at all -- that is what
 * lets whichever wall stands between the camera and the kitchen be culled -- so this is
 * not the wall's thickness so much as how far back the reveal of an opening goes before
 * the world outside starts. A stud wall with plasterboard both sides is about this.
 */
const REVEAL = 110

export class Kitchen {
  constructor(canvas, maker) {
    this.canvas = canvas
    this.maker = maker
    this.plan = null
    this.onChange = () => {}
    this.onPick = () => {}

    this.scene = new THREE.Scene()
    // The room reads as the maker's, not ours: the same colour the page is painted in.
    this.scene.background = new THREE.Color(brandOf(maker).room)

    this.renderer = new THREE.WebGLRenderer({
      canvas, antialias: true, alpha: false, powerPreference: 'high-performance',
      // So the sign-off can read a still off the canvas. Costs a buffer copy per frame,
      // which for a scene this small is cheaper than any of the alternatives.
      preserveDrawingBuffer: true })
    /*
     * Full device resolution, not the usual cap of 2.
     *
     * That cap exists because most WebGL scenes are heavy enough that rendering three times
     * per pixel costs frames. This one is four thousand triangles -- a kitchen is a dozen
     * boxes -- so there is nothing to save and everything to lose: a phone with a 3x screen
     * rendered at 2x shows visibly soft edges on every door reveal and handle, and the whole
     * pitch of this tool is that it looks like a photograph.
     */
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3))
    /*
     * Anisotropic filtering, told to the textures once.
     *
     * A floor is the one surface in this scene seen almost edge-on, stretching away from the
     * camera, and it is exactly the case trilinear filtering handles worst: without this the
     * boards go to grey mush two metres away. It is the cheapest quality setting there is on
     * hardware from the last decade, and the reason a floorboard was worth drawing at all.
     */
    setAnisotropy(this.renderer.capabilities.getMaxAnisotropy?.() ?? 8)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.05
    this.renderer.outputColorSpace = THREE.SRGBColorSpace

    this.camera = new THREE.PerspectiveCamera(42, 1, 0.05, 200)
    this.target = new THREE.Vector3()
    this.orbit = { theta: 0.62, phi: 1.32, radius: 8 }

    this.light()
    this.environment()

    this.rooms = new THREE.Group()
    this.cabinets = new THREE.Group()
    this.scene.add(this.rooms, this.cabinets)

    // How many pixels of the bottom of the canvas something else is standing on.
    this.inset = 0
    this.raycaster = new THREE.Raycaster()
    this.drag = null
    this.snapped = null

    // Observed, not called once.
    //
    // A canvas measured before the browser has laid it out is 1x1, and a renderer sized from
    // that draws the whole kitchen into a single pixel -- which looks exactly like nothing
    // rendering at all, and sends you hunting through the scene graph. Watching the element
    // is the only version of this that is right on first paint AND after a rotation.
    this.observer = new ResizeObserver(() => this.resize())
    this.observer.observe(canvas)
    this.resize()
    this.loop()
  }

  /**
   * Light it like a room, not like a CAD viewport.
   *
   * The difference between "3D model" and "photograph of a kitchen" is almost entirely
   * shadow: a soft key from a window, a warm bounce off the floor, and contact shadows where
   * the cabinets meet it.
   */
  light() {
    /*
     * The sky and the bounce off the floor.
     *
     * Turned down from 1.6, and the environment turned up to match. A hemisphere light lands
     * on every surface at once, so the more of the exposure it carries the less shape
     * anything has: the room came out evenly bright and the floor and the walls read as
     * paper. The environment map has direction in it, so the same overall brightness with
     * more of it coming from there gives a floor a near side and a far side.
     */
    this.scene.add(new THREE.HemisphereLight(0xfff4e6, 0x9c8f80, 1.15))

    const sun = new THREE.DirectionalLight(0xfff1dd, 2.2)
    sun.position.set(4, 6, 3)
    sun.castShadow = true
    sun.shadow.mapSize.set(4096, 4096)
    sun.shadow.camera.near = 0.5
    sun.shadow.camera.far = 30
    const s = 6
    Object.assign(sun.shadow.camera, { left: -s, right: s, top: s, bottom: -s })
    sun.shadow.bias = -0.0006
    sun.shadow.normalBias = 0.02
    this.scene.add(sun)

    const fill_ = new THREE.DirectionalLight(0xdfe8f0, 0.5)
    fill_.position.set(-3, 2, -2)
    this.scene.add(fill_)
  }

  /**
   * Something for the gloss to reflect.
   *
   * A two-pack gloss door and a matt laminate are the same shape and a completely different
   * kitchen, and it is the thing a homeowner is really choosing between -- but a clearcoat
   * with nothing around it to mirror renders identically to a matt one. Without this the
   * "Snow Gloss" and "Chalk" swatches produced the same room, which quietly makes half the
   * colour range pointless.
   *
   * RoomEnvironment is three's own soft studio box: a few emissive planes, generated once
   * into a cube map. It costs nothing per frame and it is the difference between a rendering
   * and a photograph.
   */
  environment() {
    const pmrem = new THREE.PMREMGenerator(this.renderer)
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
    this.scene.environmentIntensity = 0.62
    pmrem.dispose()
  }

  // ── the room ─────────────────────────────────────────────────────────────────────────

  setPlan(plan) {
    this.plan = plan
    const pick = (list, id) => (list ?? []).find((x) => x.id === id) ?? (list ?? [])[0] ?? null
    // The room around the kitchen. Read by buildRoom, which is called below.
    this.floorSpec = pick(this.maker.floors, plan.floorId)
    this.wallSpec = pick(this.maker.wallColours, plan.wallColourId)
    this.look = buildLook(
      pick(this.maker.doorStyles, plan.doorStyleId),
      pick(this.maker.colours, plan.colourId),
      pick(this.maker.benchtops, plan.benchtopId),
      pick(this.maker.handles, plan.handleId),
      pick(this.maker.splashbacks, plan.splashbackId),
      pick(this.maker.colours, plan.kickColourId),
      // What stands in the spaces the range carries: see buildRangehood, buildDishwasher
      // and buildOven. A finish, not a cabinet -- changing one moves nothing.
      {
        rangehood: pick(this.maker.rangehoods, plan.rangehoodId),
        fridge: pick(this.maker.fridges, plan.fridgeId),
        dishwasher: pick(this.maker.dishwashers, plan.dishwasherId),
        oven: pick(this.maker.ovens, plan.ovenId),
      })
    this.buildRoom()
    this.buildRuns()
    this.frame()
  }

  buildRoom() {
    this.rooms.clear()
    const walls = this.plan.walls
    const h = this.plan.roomHeight

    // The floor is the loop itself, whatever shape it is. three.js's Shape takes (x, y) and
    // ExtrudeGeometry/ShapeGeometry lay it in the XY plane, so it is drawn with y standing in
    // for z and then rotated flat -- the one sign flip that has to be right is that a
    // clockwise loop on the plan has to end up clockwise on the floor.
    const shape = new THREE.Shape()
    room.vertices(walls).forEach((v, i) => {
      if (i === 0) shape.moveTo(v.x * MM, -v.y * MM)
      else shape.lineTo(v.x * MM, -v.y * MM)
    })
    shape.closePath()
    /*
     * The floor, in whatever the maker sells.
     *
     * ShapeGeometry's UVs ARE the shape's own coordinates, which here are metres -- so the
     * texture only has to be told how many metres it covers and the boards come out 180mm
     * wide on a 4-metre floor and 180mm wide on a 6-metre one. That is a happy accident of
     * how three.js generates them and it is worth saying out loud, because the same texture
     * on a box needs sizeUv() to get the same answer.
     */
    const floorSpec = this.floorSpec
    const tex = floorSpec ? floorTexture(floorSpec) : { map: null, roughnessMap: null, span: 1 }
    const floorMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(tex.map ? 0xffffff : (floorSpec?.colour ?? 0xbfae97)),
      roughness: tex.roughnessMap ? 1 : 0.92,
    })
    if (tex.map) {
      tex.map.repeat.set(1 / tex.span, 1 / tex.span)
      floorMat.map = tex.map
    }
    if (tex.roughnessMap) {
      tex.roughnessMap.repeat.set(1 / tex.span, 1 / tex.span)
      floorMat.roughnessMap = tex.roughnessMap
    }
    const floor = new THREE.Mesh(new THREE.ShapeGeometry(shape), floorMat)
    floor.rotation.x = -Math.PI / 2
    floor.receiveShadow = true
    this.rooms.add(floor)

    /*
     * Every wall, each facing INTO the room, single-sided.
     *
     * The old room drew two walls and left the others off so the camera could see in. This
     * draws all of them and lets the renderer do that: a plane that faces inward is culled
     * from behind, so whichever walls stand between the camera and the kitchen simply are
     * not there, and whichever are behind the kitchen are. The room looks right from any
     * side without anyone choosing which walls to show.
     */
    /*
     * Painted plaster, in the colour they chose.
     *
     * The roughness map carries no colour of its own -- a painted wall IS its paint -- but a
     * wall with no variation at all takes the directional light as one clean gradient, which
     * is the most computer-looking thing in an otherwise photographic room.
     */
    const wallTex = wallTexture()
    const wallMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(this.wallSpec?.colour ?? '#F3EFE9'),
      roughness: 0.96, side: THREE.FrontSide })
    if (wallTex.roughnessMap) {
      wallTex.roughnessMap.repeat.set(1 / wallTex.span, 1 / wallTex.span)
      wallMat.roughnessMap = wallTex.roughnessMap
    }
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0xdbe6ee, roughness: 0.06, metalness: 0, transmission: 0.85,
      transparent: true, opacity: 0.35, side: THREE.DoubleSide })
    const revealMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(this.wallSpec?.colour ?? '#F3EFE9').multiplyScalar(0.94),
      roughness: 0.9, side: THREE.DoubleSide })

    /*
     * Every wall, each facing INTO the room, with its doors and windows cut out of it.
     *
     * A wall used to be one PlaneGeometry, which is why an opening could be drawn on the
     * plan and still not exist in the room. It is a Shape with a Path hole per opening now,
     * built in the wall's OWN coordinates -- x along it from its start, y up from the floor
     * -- and then stood up at the wall's start with the same rotation `runFrame` uses. That
     * is a tidier frame than the old midpoint-centred plane as well: the wall and the run
     * standing against it now agree about where zero is.
     *
     * Single-sided, so whichever walls stand between the camera and the kitchen are culled
     * from behind and the room reads from any angle without anyone choosing which to show.
     */
    for (const w of walls) {
      const len = room.length(w)
      const cuts = (this.plan.openings ?? []).filter((o) => o.wallId === w.id)

      /*
       * EVERY OPENING, CLIPPED TO THE WALL IT IS IN, ONCE.
       *
       * The hole used to be clipped here and the reveal built from the opening's RAW `at`
       * and `width` a few lines further down, which is two different rectangles for the
       * same doorway. A scan reads a wide cased opening a little wider than the wall it
       * belongs to and puts its centre a little past the end (see `readOpenings`), so the
       * hole stopped at the corner and the reveal carried on past it: a box standing in
       * mid-air beyond the end of the wall. One rectangle, computed once, used by both.
       */
      const holes = []
      for (const o of cuts) {
        const x0 = Math.max(0, o.at), x1 = Math.min(len, o.at + o.width)
        const y0 = Math.max(0, o.sill ?? 0), y1 = Math.min(h, (o.sill ?? 0) + (o.height ?? 2040))
        if (x1 - x0 < 1 || y1 - y0 < 1) continue
        holes.push({ o, x0, x1, y0, y1 })
      }

      const face = new THREE.Shape()
      face.moveTo(0, 0)
      face.lineTo(len * MM, 0)
      face.lineTo(len * MM, h * MM)
      face.lineTo(0, h * MM)
      face.closePath()
      for (const c of holes) {
        const hole = new THREE.Path()
        hole.moveTo(c.x0 * MM, c.y0 * MM)
        hole.lineTo(c.x1 * MM, c.y0 * MM)
        hole.lineTo(c.x1 * MM, c.y1 * MM)
        hole.lineTo(c.x0 * MM, c.y1 * MM)
        hole.closePath()
        face.holes.push(hole)
      }

      const plane = new THREE.Mesh(new THREE.ShapeGeometry(face), wallMat)
      plane.rotation.y = room.angle(w)
      plane.position.set(w.x1 * MM, 0, w.y1 * MM)
      plane.receiveShadow = true
      this.rooms.add(plane)

      /*
       * A hole in a wall with nothing in it reads as a hole in the world, so the opening
       * gets a LINING: the four faces of wall that the cut exposed.
       *
       * IT USED TO BE A SOLID BOX, and that box is what the owner saw as "big white slabs
       * standing in the room" -- one at every doorway. Two things made it that.
       *
       * It filled the whole opening: a `BoxGeometry` the doorway's own width and height,
       * which plugs the hole the wall has just had cut in it. Nothing can be seen through
       * a doorway with a solid box in it.
       *
       * And the walls are deliberately single-sided, so whichever ones stand between the
       * camera and the kitchen are culled and the room can be looked into from any side. A
       * closed box is not culled: it has an outside. So at every doorway on every wall the
       * camera was in front of, the wall vanished and the box stayed -- a white slab
       * standing on its own in mid-air, with no wall around it to explain what it was.
       *
       * A lining is four flat quads instead: head, sill and two jambs, each `REVEAL` deep,
       * set back into the wall's thickness and lying PERPENDICULAR to the wall face. They
       * leave the opening open, and from outside the room they are edge-on and effectively
       * invisible, which is the same disappearing act the wall itself does.
       */
      for (const c of holes) {
        const { o } = c
        const g = new THREE.Group()
        g.rotation.y = room.angle(w)
        g.position.set(w.x1 * MM, 0, w.y1 * MM)
        const wide = (c.x1 - c.x0) * MM, tall = (c.y1 - c.y0) * MM
        const x = (c.x0 + c.x1) / 2 * MM, y = (c.y0 + c.y1) / 2 * MM
        const z = -REVEAL / 2 * MM
        // Each is a flat plane turned to face into the opening; DoubleSide because which
        // side of a jamb you are looking at depends on where you stand in the room.
        const quad = (gw, gh, px, py, rotX, rotY) => {
          const m = new THREE.Mesh(new THREE.PlaneGeometry(gw, gh), revealMat)
          m.position.set(px, py, z)
          m.rotation.set(rotX, rotY, 0)
          g.add(m)
        }
        const D = REVEAL * MM
        quad(wide, D, x, c.y1 * MM, Math.PI / 2, 0)          // head, looking down
        if (c.y0 > 0.5) quad(wide, D, x, c.y0 * MM, -Math.PI / 2, 0)   // sill, looking up
        quad(D, tall, c.x0 * MM, y, 0, Math.PI / 2)          // jamb, looking along the wall
        quad(D, tall, c.x1 * MM, y, 0, -Math.PI / 2)
        if (o.kind === 'window' && wide > 100 * MM && tall > 100 * MM) {
          const pane = new THREE.Mesh(
            new THREE.PlaneGeometry(wide - 80 * MM, tall - 80 * MM), glassMat)
          pane.position.set(x, y, -20 * MM)
          g.add(pane)
        }
        this.rooms.add(g)
      }
    }
  }

  /**
   * The corner a run runs into, if the wall after it carries one.
   *
   * `theta` is the interior angle, so a splayed corner can be told apart from a square one
   * and drawn as the plain box it has to be; `return` is how far the unit reaches along the
   * second wall, which is the same number `extents` took off that run's start.
   */
  /**
   * Does a corner unit on the PREVIOUS wall reach along this run's wall?
   *
   * Which is the same question `extents` answered when it pushed this run's start: the
   * wall before this one carries a run of the same kind, so its corner unit claims the
   * first stretch of this wall.
   */
  cornerBefore(run) {
    const w = room.segment(this.plan, run.wallId)
    if (!w || room.isIsland(this.plan, run.wallId)) return false
    const prevWall = room.prev(this.plan.walls, w)
    if (!prevWall) return false
    return this.plan.runs.some((r) => r.kind === run.kind && r.wallId === prevWall.id)
  }

  cornerAt(run) {
    const w = room.segment(this.plan, run.wallId)
    if (!w || room.isIsland(this.plan, run.wallId)) return null
    const nextWall = room.next(this.plan.walls, w)
    if (!nextWall) return null
    const nextRun = this.plan.runs.find((r) => r.kind === run.kind && r.wallId === nextWall.id)
    if (!nextRun) return null
    const lim = this.maker.limits
    return {
      theta: room.corner(w, nextWall),
      return: (run.kind === 'wall' ? lim.wallCornerReturn : lim.cornerReturn) ?? 0,
    }
  }

  /**
   * Where a run sits in the room, and which way it faces.
   *
   * From the wall it is on: origin at the wall's start, local +x along the wall, local +z
   * into the room. That is all a run needs to know, and it is all a wall at any angle can
   * tell it -- the old special cases for "back" and "left" are the two rotations this
   * general one produces for those two walls.
   */
  runFrame(run) {
    const lim = this.maker.limits
    const y = run.kind === 'wall'
      ? lim.kickHeight + lim.baseHeight + lim.benchtopThickness + lim.splashbackHeight
      : lim.kickHeight
    const w = room.segment(this.plan, run.wallId)
    if (!w) return { origin: new THREE.Vector3(0, y * MM, 0), rotation: 0, length: 0, y, wall: null }
    return {
      origin: new THREE.Vector3(w.x1 * MM, y * MM, w.y1 * MM),
      rotation: room.angle(w),
      length: room.length(w),
      y,
      wall: w,
    }
  }

  buildRuns() {
    this.cabinets.clear()
    const specs = this.maker.cabinets
    const lim = this.maker.limits
    // Shared across every run, so the sink and the cooktop are placed once for the kitchen
    // rather than once per wall.
    const taken = new Set()
    const ext = room.extents(this.plan, lim)

    for (const run of this.plan.runs) {
      const frame = this.runFrame(run)
      // The corner is paid for here, once: a run that meets another around a corner starts
      // past the overlap, so two runs never stand on the same piece of floor.
      run.from = ext[run.id].from
      run.to = ext[run.id].to
      const length = run.to - run.from

      if (run.items.length === 0) {
        /*
         * A CORNER UNIT AT THE CORNER, before anything else is laid out.
         *
         * `compose` fills a length from a range; it knows nothing about corners, so a run
         * that ran into one was filled edge to edge and whatever did not divide came out as
         * a filler AT THE CORNER -- which is the one place a filler must never be, because
         * it is the one place a cabinet cannot be reached from. Every layout that is not the
         * generator's comes through here: choosing a shape, dragging a wall, turning a run
         * on. So the corner is taken out of the length first and the unit put back at the
         * end, and `compose` fills what is left.
         *
         * The corner spec is kept out of `kinds` as well: a 900 corner unit dropped into the
         * middle of a straight run is a blind cupboard against nothing.
         */
        const kinds = specs.filter((s) => s.kind === run.kind && s.role !== 'corner')
        const cornerSpec = this.cornerAt(run)
          && specs.find((s) => s.role === 'corner' && s.kind === run.kind)
        const forCorner = cornerSpec && length > cornerSpec.w + 400 ? cornerSpec.w : 0

        // `compose` rather than `fill`: the first kitchen anybody sees should look like a
        // kitchen, with a sink and a cooktop in it, not a row of identical boxes.
        const r = compose(length - forCorner, kinds, { ...lim, taken })
        run.items = r.items.map((it, i) => ({ ...it, id: `${run.id}-${i}` }))
        if (forCorner) {
          run.items.push({ id: `${run.id}-corner`, specId: cornerSpec.id, w: cornerSpec.w })
        }
        run.problem = r.problem
      }

      const group = new THREE.Group()
      group.position.copy(frame.origin)
      group.rotation.y = frame.rotation
      group.userData = { runId: run.id }

      const at = offsets(run.items)
      // Counted per run, so the alternation starts fresh on every wall.
      let hinge = 0
      run.items.forEach((item, i) => {
        // A gap draws as nothing: it is the overhead run stepping over a tall unit.
        if (item.gap) return
        /*
         * A filler has no spec, so it borrows one -- and that stand-in used to say 720 tall
         * and 560 deep whatever run it was in. In an OVERHEAD run that is a 560-deep box
         * hanging on the wall beside 320-deep cupboards: a blank slab sticking a quarter of
         * a metre further into the room than everything around it, which is what the filler
         * at the end of the left-hand overhead run looked like. It takes its run's own
         * height and depth now, which is what a filler is: a strip of the same board.
         */
        const spec = specs.find((s) => s.id === item.specId) ?? {
          id: 'filler', kind: run.kind, doors: 0, drawers: 0,
          h: run.kind === 'wall' ? lim.wallHeight : lim.baseHeight,
          d: run.kind === 'wall' ? lim.wallDepth : lim.baseDepth,
        }
        /*
         * A corner unit only draws as an L when it is actually IN the corner.
         *
         * The first version asked whether it was the last item of the run, on the reasoning
         * that the generator only ever places one at `prev.end - corner.w`. It does -- and
         * then `findSpot` bumps it along when that spot is taken, so a corner unit turns up
         * fourth of six often enough to matter. An L drawn there reaches into the middle of
         * the room.
         *
         * So the drawing asks the question it actually needs answered: does this cabinet's
         * far edge reach the end of its run, and does the wall after it carry a base run of
         * its own? Both true, and the return leg has somewhere real to go.
         */
        const corner = spec.role === 'corner'
          && run.from + at[i] + item.w >= run.to - 1
          ? this.cornerAt(run) : null
        /*
         * Which way a single door is hung.
         *
         * Alternating along the run so neighbouring singles mirror each other and their
         * handles meet at the join, the way a pair of doors does. Hung all the same way --
         * which is what happens when nothing decides -- a run of singles has a handle jammed
         * against every join and half a metre of blank door beside it, and the handles read
         * as scattered rather than as a set. A pair decides for itself and is not counted.
         */
        const single = spec.doors === 1 && !spec.drawers
        const opts = corner ? { corner } : {}
        if (single) { opts.hingeRight = hinge % 2 === 0; hinge++ }
        const c = buildCabinet(item, spec, this.look, lim, opts)
        c.position.x = (run.from + at[i]) * MM
        c.userData.runId = run.id
        c.userData.index = i
        group.add(c)

        // The sink and the cooktop ride on the cabinet that carries them, so moving that
        // cabinet moves them -- which is the only arrangement that survives a drag.
        if (spec.role === 'sink' || spec.role === 'cooktop') {
          c.add(buildAppliance(spec.role, item.w, lim, this.look.appliance))
        }
      })

      if (run.kind === 'base') {
        const isle = room.isIsland(this.plan, run.wallId)
        const overhang = isle ? (frame.wall?.overhang ?? 0) : 0
        const len = sum(run.items)

        // The benchtop, which on an island runs BACK over the seating side as well as
        // forward over the doors -- that is what people sit at.
        const top = buildBenchtop(len, lim, this.look.benchMat, { back: overhang })
        top.position.x += run.from * MM
        top.position.y -= lim.kickHeight * MM
        group.add(top)

        /*
         * AND OVER THE CORNER, when the run ends in a corner unit.
         *
         * A benchtop is one straight piece the length of its run, which is right until the
         * run ends in an L. The corner unit reaches `cornerReturn` back from the wall and
         * the stone reached only `baseDepth + overhang`, so there was a hole in the benchtop
         * exactly at the corner -- cabinet underneath, nothing over it, and the carcass top
         * showing through. The neighbouring run's stone starts past the return, so this is
         * the piece that joins them.
         */
        const lastItem = run.items[run.items.length - 1]
        const lastSpec = lastItem && specs.find((s) => s.id === lastItem.specId)
        const cornerHere = lastSpec?.role === 'corner' ? this.cornerAt(run) : null
        if (cornerHere && cornerHere.return > lim.baseDepth + lim.benchtopOverhang) {
          const front = lim.baseDepth + lim.benchtopOverhang
          const extra = cornerHere.return - front
          const patch = new THREE.Mesh(
            new THREE.BoxGeometry(lim.baseDepth * MM, lim.benchtopThickness * MM, extra * MM),
            this.look.benchMat)
          patch.position.set((run.to - lim.baseDepth / 2) * MM,
                             (lim.baseHeight + lim.benchtopThickness / 2) * MM,
                             (front + extra / 2) * MM)
          patch.castShadow = patch.receiveShadow = true
          group.add(patch)
        }

        /*
         * THE KICK, and an island's goes all the way round.
         *
         * Against a wall it is one board across the front, set back, which is what makes a
         * run look like it is standing on the floor rather than hovering over it. An island
         * has no back and no ends against anything: it is a box in the middle of a room and
         * a person walks round it, so a single board across the front left the other three
         * sides open with the floor running under the cabinets. Four boards, one per side,
         * each set back the same 60.
         */
        const SETBACK = 60
        const kickY = -lim.kickHeight / 2 * MM
        const kickPlank = (w, x, z, turn) => {
          const m = new THREE.Mesh(
            new THREE.BoxGeometry(w * MM, lim.kickHeight * MM, 1 * MM), this.look.kickMat)
          m.position.set(x * MM, kickY, z * MM)
          if (turn) m.rotation.y = Math.PI / 2
          group.add(m)
        }
        kickPlank(len, run.from + len / 2, lim.baseDepth - SETBACK)
        if (isle) {
          // The back, which on an island is under the seating overhang, and the two ends.
          const back = -overhang + SETBACK
          const depth = (lim.baseDepth - SETBACK) - back
          kickPlank(len, run.from + len / 2, back)
          kickPlank(depth, run.from + SETBACK, back + depth / 2, true)
          kickPlank(depth, run.from + len - SETBACK, back + depth / 2, true)
        }

        if (isle) {
          /*
           * An island's back is a finished panel, not a splashback and not a bare carcass.
           *
           * A run against a wall hides its back; an island's is in the middle of the room
           * and gets a door-material end panel, which is what a maker builds. And no
           * splashback: there is no wall to splash.
           */
          const backPanel = new THREE.Mesh(
            new THREE.BoxGeometry(len * MM, lim.baseHeight * MM, 18 * MM), this.look.doorMat)
          backPanel.position.set((run.from + len / 2) * MM, lim.baseHeight / 2 * MM, -9 * MM)
          backPanel.castShadow = backPanel.receiveShadow = true
          group.add(backPanel)
        } else {
          const benchTop = lim.kickHeight + lim.baseHeight + lim.benchtopThickness
          /*
           * BACK OVER THE CORNER, or there is a bare stripe of wall at the inside corner.
           *
           * A run that comes out of a corner starts a corner-claim past the wall's start --
           * that stretch is the previous run's corner unit reaching along this wall, with
           * this run's benchtop patched over it. The splashback was drawn from `run.from`,
           * so the wall above that patch got nothing: a 900mm strip of bare plaster between
           * two walls of tile, at eye level, in the corner.
           *
           * Extended backwards only as far as the CLAIM, and only when a corner is what
           * moved the start. A start pushed by a doorway must stay where it is -- tiling
           * across a door opening is a different and worse bug.
           */
          const backFill = this.cornerBefore(run)
            ? Math.min(run.from, lim.cornerReturn ?? lim.baseDepth) : 0
          const splashLen = len + backFill
          const backGeo = new THREE.PlaneGeometry(splashLen * MM, lim.splashbackHeight * MM)
          if (this.look.splash.userData?.span) {
            sizeUv(backGeo, splashLen * MM, lim.splashbackHeight * MM, this.look.splash.userData.span)
          }
          const back = new THREE.Mesh(backGeo, this.look.splash)
          back.position.set((run.from - backFill + splashLen / 2) * MM,
                            (benchTop - lim.kickHeight + lim.splashbackHeight / 2) * MM, 6 * MM)
          back.receiveShadow = true
          group.add(back)
        }
      }

      this.cabinets.add(group)
    }
    // The meshes are new; the highlight was on the old ones.
    this.drawSelection()
    this.onChange(this.plan)
  }

  /**
   * A different room.
   *
   * Somebody stands in their kitchen with a tape, or drags a wall on the plan, and the walls
   * change. Every run is re-cut to the wall it is on and then REFITTED rather than refilled,
   * so a kitchen somebody has already arranged survives being told the room is 100mm wider
   * than they thought. See `refit` for why that is not the same as filling it again.
   */
  /**
   * A door or a window added, moved or taken away.
   *
   * The same work as moving a wall, for the same reason: an opening changes what `extents`
   * says a run may occupy, so every run has to be re-cut and the room redrawn. Routed
   * through setWalls rather than repeated, so there is one description of what happens when
   * the room changes shape.
   */
  setOpenings(openings) {
    this.plan.openings = openings ?? []
    this.setWalls(this.plan.walls)
  }

  /**
   * An island added, moved, turned or taken away.
   *
   * Same work again: an island is a segment a run stands on, so changing one changes what
   * `extents` says that run may occupy, and the room has to be rebuilt around it.
   */
  setIslands(islands) {
    this.plan.islands = islands ?? []
    this.setWalls(this.plan.walls)
  }

  setWalls(walls) {
    const lim = this.maker.limits
    this.plan.walls = room.clockwise(walls)
    const ext = room.extents(this.plan, lim)

    for (const run of this.plan.runs) {
      run.from = ext[run.id].from
      run.to = ext[run.id].to
      const kinds = this.maker.cabinets.filter((s) => s.kind === run.kind)
      const r = refit(run.items, run.to - run.from, kinds, lim)
      // Kept cabinets keep the id they were dragged by; new ones get one that cannot collide
      // with them, because an id reused across a resize makes the NEXT drag pick the wrong box.
      run.items = r.items.map((it, i) => (it.id ? it : { ...it, id: `${run.id}-r${i}-${this.stamp()}` }))
      run.problem = r.problem
    }

    this.buildRoom()
    this.buildRuns()
    // Deliberately reframed even if they have turned the camera: the room they are looking at
    // is not the room any more, and leaving them at a heading chosen for the old one is how
    // you end up staring at a wall after typing a number.
    this.touched = false
    this.frame()
  }

  /** One wall, given a new length: its far end moves and the loop follows. */
  setWallLength(wallId, mm) {
    const walls = this.plan.walls.map((w) => ({ ...w }))
    const i = walls.findIndex((w) => w.id === wallId)
    if (i < 0) return
    const w = walls[i]
    const d = room.dir(w)
    const dx = w.x1 + d.x * mm - w.x2
    const dy = w.y1 + d.y * mm - w.y2
    // Slide this wall's end and everything after it, up to the wall that runs parallel to
    // this one on the far side; that wall stretches to absorb the change.
    w.x2 += dx; w.y2 += dy
    const n = walls.length
    for (let k = 1; k < n; k++) {
      const v = walls[(i + k) % n]
      const vd = room.dir(v)
      const parallel = Math.abs(vd.x * d.x + vd.y * d.y) > 0.999
      v.x1 += dx; v.y1 += dy
      if (parallel && k > 0 && k < n - 1) break   // it absorbs the move; its end stays put
      v.x2 += dx; v.y2 += dy
    }
    this.setWalls(walls)
  }

  /** Monotonic, so two ids made in the same millisecond are still different. */
  stamp() {
    this.seq = (this.seq ?? 0) + 1
    return this.seq
  }

  // ── the camera ───────────────────────────────────────────────────────────────────────

  /**
   * Put the whole kitchen on screen, from a height a person would see it from.
   *
   * Framed by projecting the room's own corners onto the camera's axes rather than from a
   * multiple of its longest side. Plumbline learned this the expensive way: a long room seen
   * from the corner covers nowhere near its longest dimension, so backing off far enough to
   * fit that leaves the building small and adrift in the middle of the pane -- and it is
   * wrong by a different amount on every screen shape, which is exactly the bug that hides
   * until somebody opens it on a phone.
   */
  frame() {
    const bb = room.bounds(this.plan.walls)
    const h = this.plan.roomHeight

    // Eye height, not the middle of the box. A kitchen is looked AT, from standing.
    this.target.set((bb.minX + bb.maxX) / 2 * MM, 1.05, (bb.minY + bb.maxY) / 2 * MM)

    // The corners to fit are the CABINETS', not the room's.
    //
    // Fitting the room means fitting a mostly-empty box: on a tall phone the kitchen ends up
    // a band across the middle with a third of the screen of blank ceiling above it. A
    // kitchen photograph is framed on the kitchen and lets the room run off the edges, and
    // so is this. The room's floor corners stay in, so the camera never ends up inside a
    // cupboard looking out.
    const box = new THREE.Box3().setFromObject(this.cabinets)
    this.corners = []
    if (!box.isEmpty()) {
      for (const x of [box.min.x, box.max.x])
        for (const y of [box.min.y, box.max.y])
          for (const z of [box.min.z, box.max.z])
            this.corners.push(new THREE.Vector3(x, y, z))
      /*
       * Aimed BELOW the middle of the kitchen, by however much of the screen the sheet is
       * covering.
       *
       * The finish sheet sits over the bottom third of the window, and the camera did not
       * know: the kitchen was centred in the whole canvas and its base cabinets -- the half
       * somebody is actually arranging -- sat underneath the swatches. Aiming low pushes the
       * subject up into the part of the screen that can be seen.
       */
      const covered = this.inset / Math.max(this.canvas.clientHeight || 1, 1)
      const height = box.max.y - box.min.y
      this.target.set((box.min.x + box.max.x) / 2,
                      Math.min((box.min.y + box.max.y) / 2 + 0.15, 1.35) - height * covered * 0.9,
                      (box.min.z + box.max.z) / 2)
    } else {
      for (const x of [bb.minX * MM, bb.maxX * MM])
        for (const y of [0, h * MM])
          for (const z of [bb.minY * MM, bb.maxY * MM])
            this.corners.push(new THREE.Vector3(x, y, z))
    }

    /*
     * Look from the OPEN side.
     *
     * The camera used to sit front-right, which was right for an L with runs on the back and
     * left walls and wrong for anything else: a U seen from front-right shows the right-hand
     * run from behind -- a row of blank carcass backs where the wall would be, since the wall
     * is culled for being between the camera and the kitchen. The doors face the middle of
     * the room, so the camera should stand where the doors point: along the average of the
     * runs' inward normals. For an L that is front-right; for a U, dead ahead; for a galley
     * the two cancel and it falls back to the front.
     */
    if (!this.touched) {
      let nx = 0, nz = 0
      for (const run of this.plan.runs) {
        if (run.kind !== 'base') continue
        const w = room.segment(this.plan, run.wallId)
        if (!w) continue
        const n = room.inward(w)
        nx += n.x; nz += n.y
      }
      if (Math.hypot(nx, nz) < 0.2) { nx = 0; nz = 1 }
      // Straight down a normal is a flat elevation, which shows no depth; lean a little.
      const theta = Math.atan2(nx, nz)
      this.orbit.theta = Math.abs(theta) < 0.15 ? 0.32 : theta * 0.85
    }

    this.place(true)
  }

  /**
   * Slide the camera's target across the screen plane, for a two-finger pan.
   *
   * `dx`/`dy` are pixels since the gesture began and `from` is where the target was then,
   * so the pan is absolute rather than accumulated -- an accumulated one drifts, because
   * every frame rounds. Clamped to the room's own bounds plus a metre: a target that can be
   * flicked anywhere is a camera that can be lost, and there is no scrollbar to get back.
   */
  panBy(dx, dy, from) {
    const { theta, phi } = this.orbit
    const dir = new THREE.Vector3(
      Math.sin(phi) * Math.sin(theta), Math.cos(phi), Math.sin(phi) * Math.cos(theta))
    const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), dir).normalize()
    const up = new THREE.Vector3().crossVectors(dir, right).normalize()

    // Pixels to metres at the target's distance: the height the frustum spans there,
    // divided by the canvas height. So a finger and the thing under it stay together.
    const h = this.canvas.clientHeight || 1
    const perPixel = 2 * this.orbit.radius * Math.tan((this.camera.fov * Math.PI / 180) / 2) / h

    const next = from.clone()
      .addScaledVector(right, -dx * perPixel)
      .addScaledVector(up, dy * perPixel)

    if (this.plan?.walls?.length) {
      const b = room.bounds(this.plan.walls)
      next.x = clamp(next.x, b.minX * MM - 1, b.maxX * MM + 1)
      next.z = clamp(next.z, -b.maxY * MM - 1, -b.minY * MM + 1)
      next.y = clamp(next.y, -0.5, (this.plan.roomHeight ?? 2400) * MM + 0.5)
    }
    this.target.copy(next)
  }

  /** Put it back where it was framed, for the button that says so. */
  recentre() {
    this.touched = false
    this.frame()
  }

  // `fit` rather than `refit`: the module-level refit() is imported above, and a parameter
  // that shadows it makes this method read as if it could call itself.
  place(fit = false) {
    const { theta, phi } = this.orbit

    const dir = new THREE.Vector3(
      Math.sin(phi) * Math.sin(theta), Math.cos(phi), Math.sin(phi) * Math.cos(theta))

    if (fit && this.corners) {
      const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), dir).normalize()
      const up = new THREE.Vector3().crossVectors(dir, right).normalize()
      const tanV = Math.tan((this.camera.fov * Math.PI / 180) / 2)
      const tanH = tanV * this.camera.aspect

      /*
       * Fit it like a photograph, not like a drawing.
       *
       * Fitting every corner is correct and, on a phone held upright, useless: a kitchen is
       * four metres wide and a phone is 390 pixels, so "all of it on screen" means the whole
       * kitchen as a band across the middle with two thirds of the screen of empty ceiling
       * and floor. Measured: 87% of the width and 25% of the height.
       *
       * A photograph of a kitchen does not do that. It fills the frame and lets the room run
       * off the edges, and the reader turns or pinches to see the rest. So on an upright
       * screen only the HEIGHT is fitted and the width is allowed to overflow; on a wide one,
       * where fitting everything is no hardship, everything is fitted.
       */
      const portrait = this.camera.aspect < 0.95
      let need = 1
      for (const c of this.corners) {
        const v = c.clone().sub(this.target)
        const depthAlong = v.dot(dir)
        const vertical = Math.abs(v.dot(up)) / tanV + depthAlong
        const horizontal = Math.abs(v.dot(right)) / tanH + depthAlong
        need = Math.max(need, portrait ? vertical : Math.max(vertical, horizontal))
      }
      this.orbit.radius = clamp(need * (portrait ? 1.5 : 1.12), 1.6, 30)
    }

    this.camera.position.copy(this.target).addScaledVector(dir, this.orbit.radius)
    this.camera.lookAt(this.target)
  }

  resize() {
    const w = this.canvas.clientWidth
    const h = this.canvas.clientHeight
    if (!w || !h) return
    this.renderer.setSize(w, h, false)
    const aspect = w / h
    const shapeChanged = Math.abs(aspect - this.camera.aspect) > this.camera.aspect * 0.05
    this.camera.aspect = aspect
    this.camera.updateProjectionMatrix()
    // Only on a real change of shape. Rotating a phone needs a refit; iOS collapsing its
    // chrome under a scroll does not, and refitting on every pixel makes the room jump.
    if (shapeChanged && this.corners && !this.touched) this.place(true)
  }

  loop = () => {
    this.renderer.render(this.scene, this.camera)
    requestAnimationFrame(this.loop)
  }

  /**
   * Put a cabinet into a run, taking the width out of whatever is next to it.
   *
   * A run is always exactly as long as its wall, so there is no such thing as "adding" width
   * -- something else has to give. The neighbour shrinks within its stretch range if it can,
   * and if it cannot the add is REFUSED and says why. That refusal is the feature: it is
   * what stops a homeowner sending a maker a run with 300mm too much in it.
   */
  add(runId, specId, at = null) {
    const run = this.plan.runs.find((r) => r.id === runId)
    const spec = this.maker.cabinets.find((c) => c.id === specId)
    if (!run || !spec) return { ok: false, why: 'not in this range' }

    const index = at ?? run.items.length
    let need = spec.w

    /*
     * Use the bare wall first.
     *
     * A run is not always as long as the wall it is on -- the back wall here is 4.2m and the
     * run is 3.6m, so there is 600mm of nothing beside it. Taking the width off the
     * neighbours while that sat empty meant "Add" refused almost every time, on a wall with
     * room for another cabinet in plain sight. Growing the run into free wall is what a
     * person means by adding one.
     */
    const wall = room.extents(this.plan, this.maker.limits)[run.id].to
    const spareWall = Math.max(0, wall - run.to)
    if (spareWall > 0) {
      const grow = Math.min(spareWall, need)
      run.to += grow
      need -= grow
    }

    // Whatever is still needed comes off the widest neighbour that can spare it.
    const givers = run.items
      .map((item, i) => ({ item, i, s: this.maker.cabinets.find((c) => c.id === item.specId) }))
      .filter((g) => g.item.filler || g.s)
      .sort((a, b) => b.item.w - a.item.w)

    const taken = []
    for (const g of givers) {
      const floor = g.item.filler ? this.maker.limits.minFiller : g.s.stretch[0]
      const spare = g.item.w - floor
      if (spare <= 0) continue
      const take = Math.min(spare, need)
      taken.push({ item: g.item, take })
      need -= take
      if (need <= 0.5) break
    }

    if (need > 0.5) {
      run.to -= spareWall - Math.max(0, spareWall - spec.w)   // put the wall back
      return { ok: false,
               why: `This wall is full. Tap a cabinet to change it into a ${spec.name} `
                  + `instead of adding one.` }
    }
    for (const t of taken) t.item.w -= t.take

    run.items.splice(index, 0, { specId: spec.id, w: spec.w, id: `${run.id}-${Date.now()}` })
    this.buildRuns()
    return { ok: true }
  }

  /** Take one out, and give its width back to its neighbours. */
  remove(runId, itemId) {
    const run = this.plan.runs.find((r) => r.id === runId)
    if (!run) return { ok: false }
    const i = run.items.findIndex((it) => it.id === itemId)
    if (i < 0) return { ok: false }
    if (run.items.length === 1) return { ok: false, why: 'a run needs something in it' }

    const [gone] = run.items.splice(i, 1)
    let give = gone.w

    // If this run had grown into bare wall, shrink it back before widening anybody: a
    // kitchen that creeps a cabinet wider every time one is removed ends up all 950s.
    const wall = this.runFrame(run).length
    if (run.to > wall - 0.5 || run.to - run.from > sum(run.items) + 0.5) {
      const shrink = Math.min(give, run.to - run.from - sum(run.items))
      if (shrink > 0.5) { run.to -= shrink; give -= shrink }
    }

    // Widen the neighbours, within what the maker will cut; anything left becomes a filler.
    const takers = run.items
      .map((item) => ({ item, s: this.maker.cabinets.find((c) => c.id === item.specId) }))
      .filter((t) => t.s)
      .sort((a, b) => (b.s.stretch[1] - b.item.w) - (a.s.stretch[1] - a.item.w))

    for (const t of takers) {
      const room = Math.min(t.s.stretch[1], this.maker.limits.maxCabinetWidth) - t.item.w
      if (room <= 0) continue
      const add = Math.min(room, give)
      t.item.w += add
      give -= add
      if (give <= 0.5) break
    }
    if (give > 0.5) {
      run.items.splice(Math.min(i, run.items.length), 0,
                       { specId: 'filler', w: give, filler: true, id: `${run.id}-f${Date.now()}` })
    }

    this.buildRuns()
    return { ok: true }
  }

  /**
   * A picture of the kitchen, from where they are standing.
   *
   * Rendered on demand rather than continuously, because `preserveDrawingBuffer` costs a
   * copy on every frame of every drag to serve one press of a button. Drawing once,
   * immediately before reading, gets the same image for nothing.
   */
  snapshot(scale = 2) {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight
    if (!w || !h) return null
    const before = this.renderer.getPixelRatio()
    try {
      this.renderer.setPixelRatio(scale)
      this.renderer.setSize(w, h, false)
      this.renderer.render(this.scene, this.camera)
      return this.canvas.toDataURL('image/jpeg', 0.86)
    } catch {
      return null
    } finally {
      this.renderer.setPixelRatio(before)
      this.renderer.setSize(w, h, false)
    }
  }

  /**
   * A still at a shape of OUR choosing, for paper.
   *
   * `snapshot` is the screen as it is, which on a phone is a tall picture with the sheet's
   * worth of floor in it. A report wants a landscape photograph of the whole kitchen: so the
   * camera is told it is that shape, framed afresh with nothing covering it, drawn once, and
   * put back exactly as it was -- orbit, inset, size and all.
   */
  still(w = 1600, h = 1000, scale = 1) {
    const cw = this.canvas.clientWidth, ch = this.canvas.clientHeight
    if (!cw || !ch) return null
    const was = { aspect: this.camera.aspect, inset: this.inset, radius: this.orbit.radius, theta: this.orbit.theta,
      touched: this.touched, ratio: this.renderer.getPixelRatio(), target: this.target.clone() }
    try {
      this.inset = 0
      this.touched = false
      this.camera.aspect = w / h
      this.camera.updateProjectionMatrix()
      this.frame()
      this.renderer.setPixelRatio(scale)
      this.renderer.setSize(w, h, false)
      this.renderer.render(this.scene, this.camera)
      return this.canvas.toDataURL('image/jpeg', 0.88)
    } catch {
      return null
    } finally {
      this.inset = was.inset
      this.touched = was.touched
      this.camera.aspect = was.aspect
      this.camera.updateProjectionMatrix()
      this.orbit.radius = was.radius
      this.orbit.theta = was.theta
      this.target.copy(was.target)
      this.place(false)
      this.renderer.setPixelRatio(was.ratio)
      this.renderer.setSize(cw, ch, false)
    }
  }

  // ── picking ──────────────────────────────────────────────────────────────────────────

  /** Which cabinet, if any, is under this point on the screen. */
  at(px, py) {
    const r = this.canvas.getBoundingClientRect()
    const p = new THREE.Vector2(
      ((px - r.left) / r.width) * 2 - 1,
      -((py - r.top) / r.height) * 2 + 1)
    this.raycaster.setFromCamera(p, this.camera)
    const hits = this.raycaster.intersectObject(this.cabinets, true)
    for (const hit of hits) {
      let o = hit.object
      while (o && o.userData.itemId === undefined) o = o.parent
      if (o) return o
    }
    return null
  }

  /**
   * Which cabinet is being talked about.
   *
   * A sheet that says "600 base, 600mm wide" is no use if you cannot see WHICH of the twelve
   * boxes it means. Two marks, because one is never enough on a phone: a soft accent rim
   * around the silhouette -- a slightly larger box drawn back-faces-only, so it shows only
   * where it escapes past the cabinet -- and a crisp edge outline that ignores the depth
   * buffer, so a cabinet chosen from the schedule can be found even when something is in
   * front of it.
   *
   * Both are children of the cabinet's own group, so they follow it through a drag without
   * anything having to keep them in step.
   */
  select(itemId) {
    this.selectedId = itemId ?? null
    this.drawSelection()
  }

  drawSelection() {
    if (this.mark) {
      this.mark.removeFromParent()
      // The two edge passes share one geometry, so dispose by identity rather than per node.
      const seen = new Set()
      this.mark.traverse((o) => {
        if (o.geometry && !seen.has(o.geometry)) { seen.add(o.geometry); o.geometry.dispose() }
        o.material?.dispose()
      })
      this.mark = null
    }
    if (!this.selectedId) return
    const g = this.meshOf(this.selectedId)
    if (!g) return
    const { w, h, d } = g.userData
    if (!w || !h || !d) return

    const accent = new THREE.Color(brandOf(this.maker).accent)
    const mark = new THREE.Group()
    // The cabinet's origin is the bottom-left of its back, so the marks centre on the middle.
    mark.position.set(w / 2 * MM, h / 2 * MM, d / 2 * MM)

    /*
     * A rim, not a wireframe.
     *
     * A slightly larger box drawn BACK faces only sits behind the cabinet from wherever the
     * camera is, so the only part of it that survives is the sliver escaping past the
     * silhouette -- an outline that follows the shape without any per-frame work. 25mm is
     * about two pixels at the distance a kitchen is looked at, which is the least that reads
     * on a phone in daylight.
     */
    const t = 25
    const shell = new THREE.Mesh(
      new THREE.BoxGeometry((w + t * 2) * MM, (h + t * 2) * MM, (d + t * 2) * MM),
      new THREE.MeshBasicMaterial({ color: accent, side: THREE.BackSide,
        transparent: true, opacity: 0.92, depthWrite: false }))
    mark.add(shell)

    /*
     * And the edges, twice.
     *
     * Once respecting depth, so the outline sits ON the cabinet like a drawn line; once
     * ignoring it, faintly, so a cabinet picked from the schedule can still be found when
     * the island or a tall unit is in front of it. Drawing only the second was a wireframe
     * box -- every hidden edge showing through, which reads as CAD rather than as "this one".
     */
    const box = new THREE.BoxGeometry((w + 2) * MM, (h + 2) * MM, (d + 2) * MM)
    const edges = new THREE.EdgesGeometry(box)
    const solid = new THREE.LineSegments(edges,
      new THREE.LineBasicMaterial({ color: accent }))
    mark.add(solid)
    const ghost = new THREE.LineSegments(edges,
      new THREE.LineBasicMaterial({ color: accent, transparent: true, opacity: 0.22,
        depthTest: false }))
    ghost.renderOrder = 999
    mark.add(ghost)

    g.add(mark)
    this.mark = mark
  }

  /** The mesh standing in for a placed cabinet, wherever it is now. */
  meshOf(itemId) {
    for (const g of this.cabinets.children) {
      const m = g.children.find((c) => c.userData.itemId === itemId)
      if (m) return m
    }
    return null
  }

  /**
   * Which leg of a chain a finger is nearest, and how far along it.
   *
   * The rail a cabinet slides on has bends in it. Each leg is a run on a wall; the finger's
   * ray is projected onto every leg's line and the nearest one wins, so a finger that has
   * gone round the corner is answered with the run round the corner. `w` is the cabinet's
   * width, so the answer is always somewhere the whole cabinet fits.
   */
  alongChain(chain, px, py, grabbed, w) {
    let best = null
    for (const run of chain.legs) {
      const { along, gap } = this.alongRunGap(run, px, py)
      const x = Math.min(Math.max(along - grabbed, run.from), Math.max(run.from, run.to - w))
      if (!best || gap < best.gap) best = { run, x, gap }
    }
    return best
  }

  /**
   * Move a cabinet from one run to another around a corner.
   *
   * The two runs are legs of one rail, so the whole rail is laid again: every cabinet in
   * chain order, the moved one in its new place, cut into legs by length and settled. The
   * cabinets round the corner make way the way beads do on a wire. If the rail cannot hold
   * them all the move is refused and nothing has changed -- the same refusal add() gives.
   */
  moveAcross(itemId, from, to, x) {
    const i = from.items.findIndex((it) => it.id === itemId)
    if (i < 0) return { ok: false, why: 'not there' }
    const item = from.items[i]
    const lim = this.maker.limits
    const { chain } = room.chainOf(this.plan, from)
    if (!chain.legs.includes(to)) return { ok: false, why: 'not on this rail' }

    // The new order: out of one leg, into the other where the finger is.
    const legsNow = chain.legs.map((r, k) => r.items.filter((it) => it.id !== itemId).map((it) => ({ ...it, leg: k })))
    const dest = legsNow[chain.legs.indexOf(to)]
    let at = 0, seen = 0
    for (const it of dest) {
      if (seen + it.w / 2 > x) break
      seen += it.w; at += 1
    }
    dest.splice(at, 0, item)

    const sequence = legsNow.flat()
    const lengths = chain.legs.map((r) => r.to - r.from)
    const specs = this.maker.cabinets.filter((c) => c.kind === chain.kind)
    const locked = new Set((this.plan.locks ?? []).map((l) => l.itemId))
    if (locked.has(itemId)) return { ok: false, why: 'That one is locked. Tap it to unlock it first.' }
    const r = reflow(sequence, lengths, specs, { ...lim, locked })
    if (r.problem) return { ok: false, why: 'That will not fit round the corner: ' + r.problem }

    chain.legs.forEach((run, k) => {
      run.items = r.legs[k].map(({ leg: _leg, ...it }) => (it.id ? it : { ...it, id: `${run.id}-f${this.stamp()}` }))
    })
    this.buildRuns()
    // Where it actually landed: the cut may have put a cabinet at the corner on the leg
    // just before or after the one the finger was nearest, which is still the corner.
    const run = chain.legs.find((leg) => leg.items.some((it) => it.id === itemId)) ?? to
    const index = run.items.findIndex((it) => it.id === itemId)
    const names = (id) => this.maker.cabinets.find((c) => c.id === id)?.name ?? id
    const said = r.swapped?.length
      ? 'Made the ' + names(r.swapped[0].from) + ' a ' + names(r.swapped[0].to) + ' to fit'
      : null
    return { ok: true, run, index, swapped: r.swapped ?? [], said }
  }

  /** How far along a run a screen point is, and how far off the run's line it sits. */
  alongRunGap(run, px, py) {
    const r = this.canvas.getBoundingClientRect()
    const p = new THREE.Vector2(
      ((px - r.left) / r.width) * 2 - 1,
      -((py - r.top) / r.height) * 2 + 1)
    this.raycaster.setFromCamera(p, this.camera)

    const frame = this.runFrame(run)
    const dir = new THREE.Vector3(Math.cos(frame.rotation), 0, -Math.sin(frame.rotation))
    const origin = frame.origin.clone()
    const ray = this.raycaster.ray
    const w0 = new THREE.Vector3().subVectors(origin, ray.origin)
    const a = dir.dot(dir), b = dir.dot(ray.direction), c = ray.direction.dot(ray.direction)
    const d = dir.dot(w0), e = ray.direction.dot(w0)
    const denom = a * c - b * b
    if (Math.abs(denom) < 1e-6) return { along: 0, gap: Infinity }
    const sc = (b * e - c * d) / denom     // along the run
    const tc = (a * e - b * d) / denom     // along the ray
    const onRun = origin.clone().addScaledVector(dir, sc)
    const onRay = ray.origin.clone().addScaledVector(ray.direction, tc)
    return { along: sc / MM, gap: onRun.distanceTo(onRay) }
  }

  /**
   * How far along its run a screen point is.
   *
   * The run is a line in the world; the finger is a ray. The answer is where the ray passes
   * closest to that line, which is the one number a drag needs -- and it stays correct at
   * any camera angle, which a screen-space delta does not.
   */
  alongRun(run, px, py) {
    const r = this.canvas.getBoundingClientRect()
    const p = new THREE.Vector2(
      ((px - r.left) / r.width) * 2 - 1,
      -((py - r.top) / r.height) * 2 + 1)
    this.raycaster.setFromCamera(p, this.camera)

    const frame = this.runFrame(run)
    const dir = new THREE.Vector3(Math.cos(frame.rotation), 0, -Math.sin(frame.rotation))
    const origin = frame.origin.clone()

    // Closest approach between the pick ray and the run's line.
    const ray = this.raycaster.ray
    const w0 = new THREE.Vector3().subVectors(origin, ray.origin)
    const a = dir.dot(dir), b = dir.dot(ray.direction), c = ray.direction.dot(ray.direction)
    const d = dir.dot(w0), e = ray.direction.dot(w0)
    const denom = a * c - b * b
    // The standard closest-approach solve gives the parameter along the FIRST line, which is
    // the run -- so it is already what we want and negating it inverts the whole gesture.
    // Dragging left moved the cabinet right, which looks like the drag being broken rather
    // than one character being wrong.
    const along = Math.abs(denom) < 1e-6 ? 0 : (b * e - c * d) / denom
    return along / MM
  }
}


const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
